import {
  APIDatetime,
  APIResource,
  APIuuid,
  IAPIArrayData,
  IAPIData,
  IAPIDataObject,
  IAPIRelationship
} from '@shared/interfaces/api.interface';
import { IQuestionSetTemplate } from '@shared/interfaces/question-set-template.interface';
import { IQuestion } from '@shared/interfaces/question.interface';
import { Question } from '@shared/models/question.model';

export class QuestionSetTemplate extends APIResource implements IQuestionSetTemplate {
  public static readonly TYPE: string = String('QuestionSetTemplate');

  id: APIuuid;
  title: string;
  language: string;
  template: string;
  is_editable: boolean;
  is_default: boolean;
  is_selectable: boolean;
  // Private templates are admin-only until an admin publishes them (VIT-1082).
  is_public = Boolean(true);

  type = String('QuestionSetTemplate');
  identifiable_type = String('Reference');
  questions: (IQuestion | IAPIRelationship)[];

  hiring_firm_id: string;
  created_at: APIDatetime;
  updated_at: APIDatetime;

  constructor(partial: Partial<IQuestionSetTemplate>) {
    super();

    Object.assign(this, partial);
  }

  public get pillsType(): any {
    let className;

    switch (this.identifiable_type) {
      case 'Reference':
        className = 'v-btn-primary';
        break;

      case 'Employer':
        className = 'v-btn-success';
        break;

      case 'Recruiter':
        className = 'v-btn-warning';
        break;

      case 'Candidate':
      default:
        className = 'v-btn-secondary';
        break;
    }

    return {
      label: this.identifiable_type,
      class: className
    };
  }

  public get pillsIsDefault(): any {
    let label;
    let className;

    if (this.is_default) {
      label = 'Highlight';
      className = 'v-btn-warning';
    }

    return {
      label,
      class: className
    };
  }

  public get pillsVisibility(): any {
    return {
      class: this.is_public ? 'v-btn-success' : 'v-btn-secondary',
      label: this.is_public ? 'Public' : 'Private'
    };
  }

  public get lockStatus(): any {
    return {
      class: this.is_editable ? 'v-btn-success' : 'v-btn-danger',
      label: this.is_editable ? 'Unlocked' : 'Locked'
    };
  }

  public get isNotSelectable(): boolean {
    return !this.is_selectable;
  }

  public get questions_count(): number {
    return this.questions.length;
  }

  public get relationship(): IAPIRelationship {
    return {
      type: QuestionSetTemplate.TYPE,
      id: this.id
    };
  }

  public get apiData(): IAPIData {
    const data = {
      data: {
        id: this.id,
        type: QuestionSetTemplate.TYPE,
        attributes: {
          title: this.title,
          identifiable_type: this.identifiable_type,
          hiring_firm_id: this.hiring_firm_id,
          is_editable: this.is_editable,
          is_default: this.is_default,
          is_public: this.is_public,
          language: this.language
        },
        relationships: {
          questions: this.questions
        }
      }
    } as IAPIData;

    return data;
  }

  static fromAPI(res: IAPIData): IQuestionSetTemplate {
    const data = res.data;
    const included = res.included;

    if (!(data && data.type === QuestionSetTemplate.TYPE)) {
      throw new Error(
        `There was a problem parsing ${QuestionSetTemplate.TYPE} API Data`
      );
    }

    const qs = new QuestionSetTemplate({
      id: data.id,
      ...data.attributes
    });

    if (included) {
      qs.questions = Question.fromAPIArray({
        data: included.filter((i: any) => i.type === 'Question'),
        included: included.filter((i: any) => i.type === 'QuestionGroup')
      });
    }

    // TO DO: match relationship with included
    return qs;
  }

  static fromAPIArray(res: IAPIArrayData): IQuestionSetTemplate[] {
    const dataArray = res.data;

    return dataArray.map((data: IAPIDataObject) => {
      const qs = new QuestionSetTemplate({
        id: data.id,
        ...data.attributes
      });

      qs.questions = data.relationships.questions as IAPIRelationship[];

      return qs;
    });
  }

  public sortQuestions() {
    this.questions.sort((q1: Question, q2: Question) =>
      q1.order < q2.order ? -1 : q1.order > q2.order ? 1 : 0
    );
  }

  public sortOptions() {
    this.questions.forEach(function(question: Question) {
      if (question['options']) {
        question['options'] = question['options'].sort((a, b) => a.order - b.order);
      }
    });
  }
}
